import { getDispatch, getState } from '../store';
import { getUserGroupById, getConferenceUserGroups, getGroups, findLanguageById } from '../store/models/selectors';
import { addGroup, refreshCallGroupAction, updateAudioAction, removeAudioFromGroupAction, removeGroupAction, moveAudioToGroupAction } from '../store/models/actions';
import { ConferenceRest, TConferenceProcessSteps, TConferenceSettings, TConferenceSettingsResult } from '../../common/rest/conference/conferenceRest';
import { CallAppEvents } from '../components/call/CallApp/hocs/events';
import { getConferenceProgressMessage, mergeProgressLog } from '../../common/utils/conferenceProgress';
import { TAudio } from '../store/models/types';
import { ELanguageStatus } from '../store/general/types';
import { setSnackbarPanelAction } from '../store/general/actions';
import { AudioRest, TRecordingInfoPatch, TRestoreInfoResult } from '../../common/rest/audio/audioRest';
import { Navigator } from './navigator';
import { EAudioPageTab } from '../components/call/AudioPage/components/types';

export class RecordingsEffects {

	// Audio ids with a live resume poll, to avoid duplicate polls.
	private static resumedAudioIds = new Set<number>();

	// Target group per audio, stashed by setConferenceSettings and applied on modal close
	private static pendingSettingsSync = new Map<number, TConferenceSettingsResult>();

	// In-flight /settings requests per audio, and audios whose modal closed before the response landed —
	// so a close that beats the response still applies the move once it arrives.
	private static inFlightSettings = new Map<number, number>();
	private static flushOnArrival = new Set<number>();

	// After reload, re-attach a poll to every conference audio still mid-run so the modal keeps
	// updating. The log was lost, so seed the current phase line and approximate the step timer
	// from updatedAt; the overall timer stays exact via startedAt.
	public static resumeProcessingPolls() {
		const dispatch = getDispatch();
		getConferenceUserGroups(getState()).forEach(group => {
			group.audios?.forEach(audio => {
				const progress = audio.progress;
				const phase = progress?.phase;
				const inProgress = !!phase && phase !== 'DONE' && phase !== 'FAILED' && phase !== 'CANCELLED';
				if (!inProgress || RecordingsEffects.resumedAudioIds.has(audio.id)) return;

				const roomId = RecordingsEffects.getRoomIdForAudio(audio);
				if (!roomId) return;

				RecordingsEffects.resumedAudioIds.add(audio.id);

				// Seed the phase line + baselines so elapsed shows at once; resets only when the step advances.
				// Anchor total via server elapsed (skew-free); step starts fresh since the server has no step start.
				if (!audio.progressLog?.length) {
					const serverElapsed = Math.max(0, progress!.updatedAt - progress!.startedAt);
					const now = Date.now();
					dispatch(updateAudioAction(audio.id, group.id, {
						progressLog: mergeProgressLog([], getConferenceProgressMessage(progress!)),
						progressStartedAt: now - serverElapsed,
						progressUpdatedAt: now,
						progressStepStartedAt: now,
					}));
				}

				CallAppEvents.pollAudioReadyByRoom(roomId, audio.id, undefined, true);
			});
		});
	}

	public static getRoomId(str: string): string {
		return str.includes('conference/')
			? str.split('conference/')[1]?.split('/')[0] || ''
			: str;
	}

	// Prefer the room over parsing it out of audioS3FilePath: a media deletion (deleteMedia('audio'),
	// or a saveLevel downgrade) clears that path, but the audio still belongs to the same conference.
	public static getRoomIdForAudio(audio?: { roomName?: string; audioS3FilePath?: string }): string {
		return audio?.roomName || RecordingsEffects.getRoomId(audio?.audioS3FilePath || '');
	}

	// Find the target audio: by id when known, else by roomId.
	private static findAudio(roomId: string, audioId?: number) {
		const match = (a: any) => audioId != null
			? a.id === audioId
			: !!a.audioS3FilePath?.includes(roomId);
		const group = getConferenceUserGroups(getState()).find(g => g.audios?.some(match));
		const audio = group?.audios?.find(match);
		return { group, audio };
	}

	// Appends a line to the audio's progress log. `error` flips progress to FAILED.
	private static pushProgressMessage(roomId: string, message: string, error?: string, audioId?: number) {
		const { group, audio } = RecordingsEffects.findAudio(roomId, audioId);
		if (!group || !audio) return;

		const prevLog = audio.progressLog || [];
		const progressLog = mergeProgressLog(prevLog, message);
		const progress = error && audio.progress
			? { ...audio.progress, phase: 'FAILED' as const, error }
			: audio.progress;
		// These one-off messages ("Starting…", "Failed: …") are each a new step.
		const stepChanged = prevLog[prevLog.length - 1] !== progressLog[progressLog.length - 1];
		const now = Date.now();
		// Anchor the total timer at the start so it ticks from 0 before the first snapshot arrives.
		const startRun = !error && audio.progressStartedAt === undefined;

		getDispatch()(updateAudioAction(audio.id, group.id, {
			progress,
			progressLog,
			...(stepChanged ? { progressStepStartedAt: now } : {}),
			...(startRun ? { progressStartedAt: now, progressUpdatedAt: now } : {}),
		}));
	}

	// Clears the log and stale progress so a restarted run doesn't inherit the old state.
	private static resetProgress(roomId: string, audioId?: number) {
		const { group, audio } = RecordingsEffects.findAudio(roomId, audioId);
		if (!group || !audio) return;

		getDispatch()(updateAudioAction(audio.id, group.id, { progress: undefined, progressLog: [], progressStartedAt: undefined, progressUpdatedAt: undefined, progressStepStartedAt: undefined }));
	}

	public static processConference(roomId: string, sourceAudioId?: number): Promise<any> {
		return ConferenceRest.process(roomId)
			.then((res) => {
				if (res?.error) {
					// No new audio on failure — report on the source tile; reset so a repeat re-opens the modal.
					RecordingsEffects.resetProgress(roomId, sourceAudioId);
					RecordingsEffects.pushProgressMessage(roomId, `Failed: ${res.error}`, res.error, sourceAudioId);
					return;
				}
				if (res?.id) {
					const dispatch = getDispatch();
					const group = getUserGroupById(getState(), res.id);
					const newAudio = res.audios?.reduce((max, a) => {
						return a.id > max.id ? a : max
					}, res.audios[0]);
					if (!newAudio.audioS3FilePath) {
						newAudio.audioS3FilePath = roomId;
					}
					if (group) {
						dispatch(refreshCallGroupAction(res));
					} else {
						dispatch(addGroup(res));
					}
					// process() creates a NEW audio; track by its id. reset clears any leftover FAILED.
					RecordingsEffects.resetProgress(roomId, newAudio.id);
					RecordingsEffects.pushProgressMessage(roomId, 'Starting…', undefined, newAudio.id);
					CallAppEvents.pollAudioReadyByRoom(roomId, newAudio.id);
				}
			})
	}

	// Current { startedAt, updatedAt } — the "stale" snapshot captured before a reprocess, so the
	// poll can skip that exact frozen snapshot until the backend actually moves it.
	private static getProgressStamp(roomId: string, audioId?: number): { startedAt: number; updatedAt: number } | undefined {
		const p = RecordingsEffects.findAudio(roomId, audioId).audio?.progress;
		return p ? { startedAt: p.startedAt, updatedAt: p.updatedAt } : undefined;
	}

	public static setConferenceSettings(roomId: string, settings: TConferenceSettings, audioId?: number): Promise<any> {
		const { group, audio } = RecordingsEffects.findAudio(roomId, audioId);

		// A saveLevel change makes the backend synchronously delete media/raw sources — not instant —
		// so show a loader on the tile for the duration, cleared below however the request settles.
		const isSaveLevelChange = settings.saveLevel !== undefined;

		// In-place field patch so the picker + LangFlag react at once;
		// the group move is deferred to flushConferenceSettings() (on modal close) so the tile doesn't remount mid-edit.
		const optimistic: Partial<TAudio> = { ...settings, ...(isSaveLevelChange ? { savingSaveLevel: true } : {}) };
		if (settings.langId != null) {
			const lang = findLanguageById(getState(), settings.langId);
			if (lang) optimistic.language = lang;
		}

		// Snapshot only the keys we're about to change so a failed request can restore them exactly.
		const prev: Partial<TAudio> = {};
		if (group && audio) {
			(Object.keys(optimistic) as (keyof TAudio)[]).forEach(k => {
				(prev as any)[k] = audio[k];
			});
			getDispatch()(updateAudioAction(audio.id, group.id, optimistic));
		}

		const revert = () => {
			if (group && audio) getDispatch()(updateAudioAction(audio.id, group.id, prev));
		};

		if (audio) {
			RecordingsEffects.inFlightSettings.set(audio.id, (RecordingsEffects.inFlightSettings.get(audio.id) || 0) + 1);
		}

		// On the last in-flight response: if the modal already closed, apply the deferred move now.
		const onSettled = () => {
			// Success path only — on error, revert() above already restored this along with everything
			// else in the snapshot. A redundant clear here on the error path is harmless.
			if (isSaveLevelChange && group && audio) {
				getDispatch()(updateAudioAction(audio.id, group.id, { savingSaveLevel: false }));
			}
			if (!audio) return;
			const left = (RecordingsEffects.inFlightSettings.get(audio.id) || 1) - 1;
			if (left > 0) {
				RecordingsEffects.inFlightSettings.set(audio.id, left);
				return;
			}
			RecordingsEffects.inFlightSettings.delete(audio.id);
			if (RecordingsEffects.flushOnArrival.delete(audio.id)) {
				RecordingsEffects.flushConferenceSettings(audio.id);
			}
		};

		return ConferenceRest.setSettings(roomId, settings)
			.then((res) => {
				if (res?.error) {
					revert();
				} else if (settings.saveLevel === 'nothing' && group && audio) {
					// The backend deletes the recording entirely at this level (conferenceSettingsAction) —
					// drop the tile now instead of the generic group-move flush, which only moves an audio
					// between groups and would otherwise leave a stale, already-deleted entry on screen.
					getDispatch()(removeAudioFromGroupAction(audio.id, group.id));
					RecordingsEffects.removeGroupIfEmpty(group.id);
				} else if (audio) {
					// A downgrade deletes the media the new level no longer allows (conferenceSettingsAction)
					const fresh = group && res?.audios?.find(a => a.id === audio.id);
					if (fresh) {
						getDispatch()(updateAudioAction(audio.id, group.id, {
							url: fresh.url,
							audioS3FilePath: fresh.audioS3FilePath,
							videoUrl: fresh.videoUrl,
							roomName: fresh.roomName,
						}));
					}
					// Stash the authoritative target group; the actual move happens on modal close.
					if (res?.id) RecordingsEffects.pendingSettingsSync.set(audio.id, res);
					else RecordingsEffects.pendingSettingsSync.set(audio.id, {} as TConferenceSettingsResult);
				}
				onSettled();
				return res;
			})
			.catch((e) => {
				revert();
				onSettled();
				throw e;
			});
	}

	// Apply the deferred settings sync for an audio (called when the settings modal closes):
	// refresh/add the target group and move the audio out of its old group.
	public static flushConferenceSettings(audioId: number) {
		// Closed before the /settings response — apply the move once it lands.
		if (!RecordingsEffects.pendingSettingsSync.has(audioId) && RecordingsEffects.inFlightSettings.has(audioId)) {
			RecordingsEffects.flushOnArrival.add(audioId);
			return;
		}
		if (!RecordingsEffects.pendingSettingsSync.has(audioId)) return;
		const res = RecordingsEffects.pendingSettingsSync.get(audioId);
		RecordingsEffects.pendingSettingsSync.delete(audioId);

		const dispatch = getDispatch();
		const state = getState();
		const oldGroup = getConferenceUserGroups(state).find(g => g.audios?.some(a => a.id === audioId));
		const audio = oldGroup?.audios?.find(a => a.id === audioId);
		if (!oldGroup || !audio) return;

		// 1) Authoritative target group from the backend (handles a brand-new language group).
		if (res?.id && res.id !== oldGroup.id) {
			if (getUserGroupById(state, res.id)) dispatch(refreshCallGroupAction(res));
			else dispatch(addGroup(res));
			RecordingsEffects.moveOutOfOldGroup(audioId, oldGroup.id);
			return;
		}

		// 2) Fallback: move to the existing conference group for the audio's (new) language.
		// Skip when the old group already serves that language (e.g. only provider/multilingual changed).
		const langCode = audio.langId != null ? findLanguageById(state, audio.langId)?.code : audio.language?.code;
		const stayInOldGroup = !!langCode && !!oldGroup.languages?.some(l => l.code === langCode && l.status === ELanguageStatus.TO_LEARN);
		const targetGroup = langCode && !stayInOldGroup
			? getConferenceUserGroups(state).find(g => g.id !== oldGroup.id && g.languages?.some(l => l.code === langCode))
			: undefined;
		if (targetGroup) {
			dispatch(moveAudioToGroupAction(audioId, oldGroup.id, targetGroup.id));
			RecordingsEffects.removeGroupIfEmpty(oldGroup.id);
		}
	}

	// Drop the audio from its old group after it was refreshed/added into the target; clean up if empty.
	private static moveOutOfOldGroup(audioId: number, oldGroupId: number) {
		getDispatch()(removeAudioFromGroupAction(audioId, oldGroupId));
		RecordingsEffects.removeGroupIfEmpty(oldGroupId);
	}

	private static removeGroupIfEmpty(groupId: number) {
		const group = getUserGroupById(getState(), groupId);
		if (group && !group.audios?.length) getDispatch()(removeGroupAction(groupId));
	}

	// Abort an in-flight process/reprocess. The poll keeps running and lands on CANCELLED (or DONE
	// if nothing was running).
	public static cancelConference(roomId: string, audioId?: number): Promise<any> {
		return ConferenceRest.cancel(roomId)
			.then((res) => {
				if (res?.error) {
					getDispatch()(setSnackbarPanelAction(true, res.error));
					return;
				}
				if (res?.cancelling) {
					RecordingsEffects.pushProgressMessage(roomId, 'Cancelling…', undefined, audioId);
				}
			})
			.catch((e) => {
				console.error(e);
				getDispatch()(setSnackbarPanelAction(true, 'Failed to cancel'));
			});
	}

	// Delete an unprocessed conference recording: call the backend, then drop the audio from its
	// group (removing the group if it's left empty).
	public static deleteConference(roomId: string, audioId?: number): Promise<any> {
		const { group } = RecordingsEffects.findAudio(roomId, audioId);
		return ConferenceRest.delete(roomId)
			.then(() => {
				if (audioId != null && group) {
					getDispatch()(removeAudioFromGroupAction(audioId, group.id));
					RecordingsEffects.removeGroupIfEmpty(group.id);
				}
			})
			.catch((e) => {
				console.error(e);
				const error = e?.response?.data?.error || 'Failed to delete';
				getDispatch()(setSnackbarPanelAction(true, error));
			});
	}

	public static reprocessConference(
		roomId: string,
		audioId?: number,
		force?: boolean,
		steps?: TConferenceProcessSteps
	): Promise<any> {
		// Capture the current snapshot BEFORE reset wipes it, so the poll can skip that exact frozen
		// snapshot until the reprocess run moves it (rotates startedAt or bumps updatedAt).
		const stale = RecordingsEffects.getProgressStamp(roomId, audioId);
		RecordingsEffects.resetProgress(roomId, audioId);
		RecordingsEffects.pushProgressMessage(roomId, 'Starting…', undefined, audioId);
		// Fire-and-forget like process(): the poll drives progress, not the response.
		// awaitNewRun: this poll starts before the request below has registered the run, so it must not
		// mistake the previous run's still-COMPLETED status for this one finishing.
		const stopPolling = CallAppEvents.pollAudioReadyByRoom(roomId, audioId, stale, false, true);
		return ConferenceRest.reprocess(roomId, force, steps)
			.then((res) => {
				if (res?.error) {
					stopPolling();
					RecordingsEffects.pushProgressMessage(roomId, `Failed: ${res.error}`, res.error, audioId);
				}
			})
			.catch((e) => {
				console.error(e);
				// A gateway timeout / network drop likely means the job still runs on the backend — keep
				// polling. Any other HTTP error means it never started, so stop the poll and report.
				const status: number | undefined = e?.response?.status;
				const backendMayRun = status === undefined || [408, 502, 503, 504].includes(status);
				if (!backendMayRun) {
					stopPolling();
					const detail = `request failed (${status})`;
					RecordingsEffects.pushProgressMessage(roomId, `Failed: ${detail}`, detail, audioId);
				}
			});
	}

	public static findGroupId(audioId: number): number | undefined {
		return getGroups(getState()).find(g => g.audios?.some((a: TAudio) => a.id === audioId))?.id;
	}

	public static openAudioTab(audioId: number, tab: EAudioPageTab): void {
		const groupId = RecordingsEffects.findGroupId(audioId);
		if (groupId) Navigator.openAudio(groupId, audioId, tab);
	}

	public static async saveInfo(audioId: number, info: TRecordingInfoPatch): Promise<void> {
		try {
			const res = await AudioRest.saveInfo(audioId, info);
			if (!res) throw new Error('empty response');
			const group = getGroups(getState()).find(g => g.audios?.some((a: TAudio) => a.id === audioId));
			if (group) getDispatch()(updateAudioAction(audioId, group.id, res));
		} catch (e) {
			console.error('RecordingsEffects.saveInfo: failed to save title/description', e);
			getDispatch()(setSnackbarPanelAction(true, 'Failed to save title and description'));
		}
	}

	// true when the new title/description got saved
	public static async generateInfo(audioId: number, first: boolean): Promise<boolean> {
		try {
			const res = await AudioRest.generateInfo(audioId, first);
			if (!res?.title) throw new Error('empty response');
			const { nativeFailed, ...info } = res;
			const group = getGroups(getState()).find(g => g.audios?.some((a: TAudio) => a.id === audioId));
			if (group) getDispatch()(updateAudioAction(audioId, group.id, info));
			// the target is new, but the native copy is still the previous one
			if (nativeFailed) getDispatch()(setSnackbarPanelAction(true, 'Failed to translate title and description into your native language'));
			return true;
		} catch (e) {
			console.error('RecordingsEffects.generateInfo: failed to generate title/description', e);
			getDispatch()(setSnackbarPanelAction(true, 'Failed to generate title and description'));
			return false;
		}
	}

	// 'gone': the server no longer has the previous state (expired, restart) — retrying won't help
	public static async restoreInfo(audioId: number): Promise<TRestoreInfoResult> {
		try {
			const res = await AudioRest.restoreInfo(audioId);
			if (!res) throw new Error('empty response');
			const group = getGroups(getState()).find(g => g.audios?.some((a: TAudio) => a.id === audioId));
			if (group) getDispatch()(updateAudioAction(audioId, group.id, res));
			return 'restored';
		} catch (e: any) {
			if (e?.response?.status === 409) {
				getDispatch()(setSnackbarPanelAction(true, 'The previous title and description can no longer be restored'));
				return 'gone';
			}
			console.error('RecordingsEffects.restoreInfo: failed to restore title/description', e);
			getDispatch()(setSnackbarPanelAction(true, 'Failed to restore the previous title and description'));
			return 'failed';
		}
	}

}
